import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { BaseKendoList } from '@common/classes/base-kendo-list';
import { GridColumnConfigController } from '@common/classes/grid-column-config-controller';
import { ConfigurableGridColumn } from '@common/components/column-configurator/column-configurator.component';
import { ManipulationCodes } from '@common/knownTypes/ManipulationCodes';
import { ReleaseOrderTypeCodes } from '@common/knownTypes/ReleaseOrderTypeCodes';
import { PagingAndOrderingModel } from '@common/models/common/PagingAndOrdering.model';
import { ReleaseOrderService } from '@common/services/release-order.service';
import { ServiceRequestService } from '@common/services/service-request.service';
import { currentUserSubject } from '@common/services/user.service';
import { marker } from '@common/translate-marker';
import { requiredValidator } from '@common/validators/required-validator';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { GridComponent, PageChangeEvent, RowArgs, RowClassArgs } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { ContainerFromBulPCSListFilter } from 'app/navis/models/ContainerFromBulPCSListFilter';
import { BaseKendoListService } from 'app/singleton-services/base-kendo-list.service';
import { Observable } from 'rxjs';
import { distinctUntilChanged, finalize, map, shareReplay, startWith, tap } from 'rxjs/operators';

@Component({
    selector: 'app-add-containers-from-bulpcs',
    templateUrl: './add-containers-from-bulpcs.component.html',
    styleUrls: ['./add-containers-from-bulpcs.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddContainersFromBulPCSComponent extends BaseKendoList implements OnInit, AfterViewInit {
    override applyStateFilter: boolean = false;

    isBusy = false;
    filter: ContainerFromBulPCSListFilter = new ContainerFromBulPCSListFilter();
    location: Location;
    bookingVisible = true;
    title: string;
    message: string;
    cargoManifestId: number;
    manipulationId: string;
    notificationTypeId: string;
    fromServiceRequest: boolean;
    shipmentType: string;
    tosVoyageNumber: string;
    yesButtonName: string;
    noButtonName: string;
    cancelButtonName: string;
    formReady: boolean = false;
    agentId: number;
    customerId: number;
    serviceRequestId: number;
    additionalServices: any[];
    showBookingOrBLAlert: boolean = false;
    showVSOUTalert: boolean = false;
    shippingLineId: number;
    releaseOrderTypeId: string;
    releaseOrderId: number;
    matchingShippingLines: any;
    agentMatchingShippingLines: number[];
    vesselVisitId: number;
    voyageNumber: string;
    departureVesselVisitId: number;
    loadingVoyageNumber: string;
    isVesselAgent: boolean = false;
    hasVesselContainerLineBypass: boolean = false;
    releaseOrderTooltip: boolean = false;

    autoLoadData = false;

    line$: Observable<any>;
    lines: any[] = [];
    vesselVisits$: Observable<any>;
    eitherBookingOrBLValidaorRef = this.eitherBookingOrBLValidator();
    private appliedSearchCriteria: any;
    private appliedSocImportSearch = false;
    private socImportCriteriaVersion = 0;
    private completedSocImportCriteriaVersion = -1;

    get isSocImportPickupDialog(): boolean {
        return (
            this.fromServiceRequest &&
            !this.releaseOrderId &&
            [ManipulationCodes.TROUT, ManipulationCodes.RLOUT].includes(this.manipulationId)
        );
    }

    get isSocImportSearch(): boolean {
        const value = this.form?.controls['line']?.value;
        const lineId = value?.id ?? value;
        return (
            this.isSocImportPickupDialog &&
            this.lines.some(
                (line) => Number(line.id) === Number(lineId) && ReleaseOrderTypeCodes.SocLineCodes.includes(line.code)
            )
        );
    }

    private hasSearchKeys(value: string): boolean {
        return value?.split(',').some((key) => !!key.trim()) ?? false;
    }

    private invalidateSocImportSearch(): void {
        this.form.updateValueAndValidity({ emitEvent: false });
    }

    disabledSocImportSelection(): boolean {
        return (
            this.isSocImportPickupDialog &&
            (!this.appliedSearchCriteria || this.completedSocImportCriteriaVersion !== this.socImportCriteriaVersion)
        );
    }

    // Override methods that trigger URL state management to prevent dialog from storing state in URL
    public sortChange(sort: SortDescriptor[]): void {
        if (this.isBusy || (!this.appliedSearchCriteria && this.disabledSearch())) {
            return;
        }

        this.sortsArr = sort;
        this.search(
            this.appliedSearchCriteria ? { getRawValue: () => this.appliedSearchCriteria } : this.form,
            this.listQuery(this.filter)
        );
    }

    protected override defaultSort() {
        this.sortsArr = [{ field: 'articleNumber', dir: 'asc' }];
    }

    public pageChange(event: PageChangeEvent): void {
        if (this.isBusy || (!this.appliedSearchCriteria && this.disabledSearch())) {
            return;
        }

        this.pageObj.skip = event.skip;
        this.pageObj.take = event.take;
        this.filter.offset = event.skip;
        this.filter.pageSize = event.take;
        this.search(
            this.appliedSearchCriteria ? { getRawValue: () => this.appliedSearchCriteria } : this.form,
            this.listQuery(this.filter)
        );
    }

    search(form, listQuery) {
        if (!this.appliedSearchCriteria && this.disabledSearch()) {
            this.isBusy = false;
            return;
        }
        this.isBusy = true;
        // Use the protected method from BaseKendoList without URL state management
        this.filter.offset = this.pageObj.skip;
        this.filter.pageSize = this.pageObj.take;

        for (const order of this.sortsArr) {
            if (order['dir']) {
                this.filter.orderBy = order['field'];
                this.filter.orderDirection = order['dir'];
            }
        }

        // Safely assign form values to filter
        const formValues = form.getRawValue();
        Object.keys(formValues).forEach((key) => {
            const value = formValues[key];

            if (key.includes('Ids') || key.includes('ids')) {
                if (Array.isArray(value)) {
                    this.filter[key] = value;
                } else if (value === null || value === undefined || value === 0 || value === '') {
                    this.filter[key] = [];
                } else {
                    this.filter[key] = [value];
                }
            } else {
                this.filter[key] = value;
            }
        });

        const criteriaVersion = this.socImportCriteriaVersion;
        this.getQueryAction(
            listQuery.pipe(
                map((result: any) => {
                    if (criteriaVersion !== this.socImportCriteriaVersion) {
                        return { data: [], count: 0 };
                    }
                    this.completedSocImportCriteriaVersion = criteriaVersion;
                    return result;
                })
            )
        );
        // Intentionally not calling updateUrlWithState() to prevent URL updates in modal
    }

    @ViewChild(GridComponent) public grid: GridComponent;

    selected: any[] = [];

    selectAll: boolean = false;

    configurableColumns: ConfigurableGridColumn[] = [
        { key: 'articleNumber', title: marker('Article Nbr.') },
        { key: 'container', title: marker('Container') },
        { key: 'status.id', title: marker('Status') },
        { key: 'fullOrEmpty', title: marker('F/E') },
        { key: 'iso', title: marker('ISO') },
        { key: 'type', title: marker('Type') },
        { key: 'length', title: marker('Length') },
        { key: 'height', title: marker('Height') },
        { key: 'dischargingMeanOfTransport', title: marker('Disch. MOT') },
        { key: 'vesselVoyage', title: marker('Vessel Voyage Nbr.') },
        { key: 'line', title: marker('Line') },
        { key: 'booking', title: marker('Booking') },
        { key: 'billOfLading', title: marker('Bill Of Lading') },
        { key: 'nbrOfPackages', title: marker('Nbr. of Packages') },
        { key: 'grossWeight', title: marker('Gross Weight') },
        { key: 'netWeight', title: marker('Net Weight') },
        { key: 'cargoDescription', title: marker('Cargo Description') },
        { key: 'consignee', title: marker('Consignee'), isAvailable: () => !!this.releaseOrderId }
    ];
    readonly columnConfig = new GridColumnConfigController(
        'common.addContainersFromBulPCS',
        () => this.configurableColumns,
        () => this.grid,
        () => this.refreshColumnLayout()
    );

    get isVSOUTServiceRequest(): boolean {
        return this.manipulationId === 'VSOUT' && this.serviceRequestId !== null;
    }

    get showUnloadingVoyageNumber(): boolean {
        return (
            ManipulationCodes.exitMotManipulations.includes(this.manipulationId) ||
            this.manipulationId === ManipulationCodes.ADDS
        );
    }

    constructor(
        baseKendoListService: BaseKendoListService,
        public activeModal: DialogRef,
        private cdr: ChangeDetectorRef,
        private serviceRequestService: ServiceRequestService,
        private releaseOrderService: ReleaseOrderService
    ) {
        super(baseKendoListService);
        this.location = location;

        currentUserSubject.subscribe((user) => {
            this.user = user;
        });

        this.setSelectableSettings();
    }

    // Override resetPaging to ignore URL page parameters for modal dialogs
    protected resetPaging(take: number = 15, skip: number = 0): void {
        // Always use default values, ignore URL parameters for modals
        this.pageObj = { take, skip, totalElements: 0 };
    }

    ngOnInit(): void {
        this.columnConfig.refresh();
        // Initialize pagination, sorting, and form like the base class but without URL parameter handling
        this.resetPaging();
        this.defaultSort();
        this.filter = new ContainerFromBulPCSListFilter(
            new PagingAndOrderingModel(this.pageObj.skip, this.pageObj.take),
            new ContainerFromBulPCSListFilter()
        );
        this.form = this.filter.createFormGroup();
        this.initialFilterValues = this.form.value;
        this.excelfileName = this.title + '.xlsx';
        this.pdfFileName = this.title + '.pdf';

        // Skip URL parameter handling and state service logic for modal dialogs
        // The modal should not restore or save state to URL
    }

    ngAfterViewInit(): void {
        this.filter = new ContainerFromBulPCSListFilter();

        if (this.manipulationId == 'VSOUT' && this.hasVesselContainerLineBypass) {
            this.showVSOUTalert = true;
        }

        this.line$ = this.serviceRequestService
            .getLinesForContainers(this.agentId, this.cargoManifestId, this.serviceRequestId)
            .pipe(
                tap((lines: any[]) => {
                    this.lines = lines;
                    this.updateSearchCriteriaValidation(this.form.controls['line'].value);
                    if (this.isSocImportPickupDialog && this.form) {
                        this.form.controls['line'].updateValueAndValidity();
                    }
                }),
                shareReplay(1)
            );
        this.filter.serviceRequestId = this.serviceRequestId;
        this.vesselVisits$ = this.serviceRequestService.getVesselVisitsForFromBulPCS(this.filter).pipe(
            tap((vesselVisits: any[]) => this.applyVSOUTServiceRequestDefaults(vesselVisits)),
            shareReplay(1)
        );
        if (Number(this.agentId) === Number(this.customerId)) {
            this.form.controls['line'].clearValidators();
            this.form.controls['booking'].clearValidators();
            this.form.controls['billsOfLading'].clearValidators();

            this.form.controls['line'].updateValueAndValidity();
            this.form.controls['booking'].updateValueAndValidity();
            this.form.controls['billsOfLading'].updateValueAndValidity();
        } else {
            this.form.controls['line'].setValidators(requiredValidator());
            this.form.controls['line'].updateValueAndValidity();
            this.form.setValidators(this.eitherBookingOrBLValidaorRef);
            this.form.updateValueAndValidity({ emitEvent: false });
            this.showBookingOrBLAlert = true;
        }

        if (this.notificationTypeId !== null) {
            this.manipulationId = this.notificationTypeId == 'Arrival' ? 'VSIN' : 'VSOUT';
        }

        if (this.shippingLineId) {
            this.setReleaseOrderControls();
        }

        const setVoyageNumber = this.releaseOrderId
            ? (vesselVisitId: number) => this.populateReleaseOrderVoyageNumber(vesselVisitId)
            : (vesselVisitId: number) => this.populateVoyageNumber(vesselVisitId);

        this.form.controls['vesselVisitId'].valueChanges
            .pipe(startWith(this.form.controls['vesselVisitId'].value), distinctUntilChanged())
            .subscribe(setVoyageNumber);

        if (this.manipulationId == 'VSOUT' && this.hasVesselContainerLineBypass) {
            this.form.controls['line'].clearValidators();
            this.form.controls['line'].updateValueAndValidity();
        }

        if (this.includeReleaseOrderCheck()) {
            this.form.removeValidators(this.eitherBookingOrBLValidaorRef);
            this.form.updateValueAndValidity({ emitEvent: false });
            this.showBookingOrBLAlert = false;
            this.form.controls['releaseOrderNumber'].setValidators(requiredValidator());
            this.form.controls['releaseOrderNumber'].updateValueAndValidity();

            this.form.controls['line'].valueChanges.subscribe((value) => {
                if (this.isSocImportSearch) {
                    this.releaseOrderTooltip = false;
                    this.form.controls['releaseOrderNumber'].clearValidators();
                    this.form.controls['releaseOrderNumber'].updateValueAndValidity();
                    return;
                }
                if (value) {
                    this.releaseOrderTooltip = false;
                    const changedLine = this.lines.find((obj) => obj.id === value);
                    if (this.matchingShippingLines.includes(changedLine?.id) || this.user?.isSuperAdmin) {
                        this.form.controls['releaseOrderNumber'].clearValidators();
                        this.form.controls['releaseOrderNumber'].updateValueAndValidity();
                    } else {
                        if (
                            this.shipmentType == 'FULLCNT' &&
                            ['ADDS', 'FCFS', 'RLOUT', 'TCFS', 'TROUT'].includes(this.manipulationId)
                        ) {
                            this.releaseOrderTooltip = true;
                            this.form.controls['releaseOrderNumber'].clearValidators();
                            this.form.controls['releaseOrderNumber'].updateValueAndValidity();
                        } else {
                            this.form.controls['releaseOrderNumber'].setValidators(requiredValidator());
                            this.form.controls['releaseOrderNumber'].updateValueAndValidity();
                        }
                    }
                } else {
                    this.form.controls['releaseOrderNumber'].setValidators(requiredValidator());
                    this.form.controls['releaseOrderNumber'].updateValueAndValidity();
                }
            });
        } else {
            this.form.controls['line'].valueChanges.subscribe((value) => {
                this.updateSearchCriteriaValidation(value);
            });
            this.form.controls['containers'].valueChanges.subscribe(() => {
                this.updateSearchCriteriaValidation(this.form.controls['line'].value);
            });
        }

        if (this.isVSOUTServiceRequest) {
            //this.clearAllValidatorsButLine();
        }

        setTimeout(() => {
            if (this.shipmentType === 'FULLCNT') {
                this.setInactiveFilterValue('fullOrEmpty', 'F');
            } else if (this.shipmentType === 'NONCNTC' && this.additionalServices !== null) {
                if (this.additionalServices.some((x) => x.additionalServiceId === 'CONTE')) {
                    this.setInactiveFilterValue('fullOrEmpty', 'E');
                } else if (this.additionalServices.some((x) => x.additionalServiceId === 'DECON')) {
                    this.setInactiveFilterValue('fullOrEmpty', 'F');
                }
            } else if (this.shipmentType === 'EMPTYCNT') {
                this.setInactiveFilterValue('fullOrEmpty', 'E');
            }
        }, 1500);

        if (this.vesselVisitId) {
            const control = this.form.controls['vesselVisitId'];
            control.setValue(this.vesselVisitId);
            if (this.releaseOrderId) control.disable();
        }

        if (this.voyageNumber) {
            const control = this.releaseOrderId
                ? this.form.controls['unloadingVoyageNumber']
                : this.form.controls['loadingVoyageNumber'];
            control.setValue(this.voyageNumber);
            if (this.releaseOrderId) control.disable();
        }

        this.form.controls['billsOfLading']?.addValidators(Validators.maxLength(2048));
        this.form.controls['billsOfLading']?.updateValueAndValidity();
        this.form.controls['containers']?.addValidators(Validators.maxLength(2048));
        this.form.controls['containers']?.updateValueAndValidity();

        if (this.isSocImportPickupDialog) {
            this.form.addValidators((group) => {
                if (
                    this.isSocImportSearch &&
                    (!this.hasSearchKeys(group.get('containers')?.value) ||
                        !this.hasSearchKeys(group.get('billsOfLading')?.value))
                ) {
                    return { socImportCriteria: true };
                }
                return null;
            });
            this.form.valueChanges.subscribe(() => this.invalidateSocImportSearch());
            this.form.controls['line'].updateValueAndValidity();
            this.invalidateSocImportSearch();
        }

        this.cdr.detectChanges();
    }

    clearAllValidatorsButLine() {
        Object.keys(this.form.controls).forEach((key) => {
            if (key !== 'line') {
                this.form.controls[key].clearValidators();
                this.form.controls[key].updateValueAndValidity();
            }
        });

        this.form.removeValidators(this.eitherBookingOrBLValidaorRef);
        this.form.updateValueAndValidity({ emitEvent: false });
    }

    eitherBookingOrBLValidator(): ValidatorFn {
        return (group: FormGroup): ValidationErrors | null => {
            const booking = group.controls['booking'].value;
            const billsOfLading = group.controls['billsOfLading'].value;
            const loadingVoyageNumber = group.controls['loadingVoyageNumber'].value;
            const vesselVisitId = group.controls['vesselVisitId'].value;
            const lineId = group.controls['line'].value;
            const containers = group.controls['containers'].value;

            if (this.manipulationId == 'VSOUT' && this.hasVesselContainerLineBypass) {
                if (
                    booking ||
                    billsOfLading ||
                    loadingVoyageNumber ||
                    vesselVisitId ||
                    this.isSocNumberOnlySearch(lineId, containers)
                ) {
                    return null;
                } else {
                    return { eitherBookingOrBillOfLading: true };
                }
            }

            if ((booking && !billsOfLading) || (!booking && billsOfLading) || (booking && billsOfLading)) {
                return null;
            } else {
                return { eitherBookingOrBillOfLading: true };
            }
        };
    }

    private updateSearchCriteriaValidation(lineId: any) {
        const selectedLineId = lineId?.id ?? this.lines.find((obj) => obj.id === lineId)?.id ?? lineId;
        const matchingLineIds = this.agentMatchingShippingLines ?? this.matchingShippingLines;
        const lineMatchesAgent = matchingLineIds?.includes(selectedLineId);
        const socNumberOnlySearch = this.isSocNumberOnlySearch(selectedLineId, this.form.controls['containers'].value);

        if (
            this.isSocImportSearch ||
            (!!this.releaseOrderId &&
                this.releaseOrderTypeId !== ReleaseOrderTypeCodes.Soc &&
                this.lines.some(
                    (line) =>
                        Number(line.id) === Number(selectedLineId) &&
                        ReleaseOrderTypeCodes.SocLineCodes.includes(line.code)
                )) ||
            (lineId && (lineMatchesAgent || this.user?.isSuperAdmin || socNumberOnlySearch))
        ) {
            this.form.removeValidators(this.eitherBookingOrBLValidaorRef);
            this.showBookingOrBLAlert = false;
        } else {
            this.form.addValidators(this.eitherBookingOrBLValidaorRef);
            this.showBookingOrBLAlert = true;
        }

        this.form.updateValueAndValidity({ emitEvent: false });
    }

    private isSocNumberOnlySearch(lineId: any, containers: string): boolean {
        if (!this.isVSOUTServiceRequest || !this.hasVesselContainerLineBypass || !containers?.trim()) {
            return false;
        }

        const selectedLineId = lineId?.id ?? this.lines.find((obj) => obj.id === lineId)?.id ?? lineId;
        const selectedLine = this.lines.find((obj) => Number(obj.id) === Number(selectedLineId));
        return selectedLine?.code === ReleaseOrderTypeCodes.SocLine;
    }

    private applyVSOUTServiceRequestDefaults(vesselVisits: any[]) {
        if (this.manipulationId !== 'VSOUT' || this.releaseOrderId) {
            return;
        }

        this.setInactiveFilterValue('inboundVessels', false, false);

        if (
            this.departureVesselVisitId &&
            vesselVisits?.some((x) => Number(x.id) === Number(this.departureVesselVisitId))
        ) {
            this.setInactiveFilterValue('vesselVisitId', this.departureVesselVisitId, false);
        }

        this.setInactiveFilterValue('loadingVoyageNumber', this.loadingVoyageNumber ?? null, false);
        this.form.updateValueAndValidity({ emitEvent: false });
    }

    private setInactiveFilterValue(key: string, value: string | number | boolean | null, emitEvent: boolean = true) {
        this.form.controls[key].setValue(value, { emitEvent });
        this.filter[key] = value;
    }

    public listQuery(filter: ContainerFromBulPCSListFilter) {
        this.isBusy = true;
        filter.serviceRequestId = this.serviceRequestId;
        filter.agentId = this.agentId;
        filter.releaseOrderId = this.releaseOrderId;
        if (this.releaseOrderId) {
            filter.line = this.shippingLineId.toString();
        }
        return this.serviceRequestService.getContainersFromBulPCS(filter);
    }

    override searchForm(): void {
        if (this.isBusy || this.disabledSearch()) {
            return;
        }
        if (this.isSocImportPickupDialog) {
            this.appliedSearchCriteria = { ...this.form.getRawValue() };
            this.appliedSocImportSearch = this.isSocImportSearch;
            this.socImportCriteriaVersion++;
            this.selected = [];
            this.selectAll = false;
            this.itemsGridView = { data: [], total: 0 };
            this.gridData = [];
            this.resetPaging();
            this.search(
                this.appliedSearchCriteria ? { getRawValue: () => this.appliedSearchCriteria } : this.form,
                this.listQuery(this.filter)
            );
        } else {
            super.searchForm();
        }
        if (this.selectAll) {
            this.selectAll = false;
            this.selected = [];
        }
    }

    override clearFilter() {
        this.form.reset();
        if (this.shipmentType === 'FULLCNT') {
            this.setInactiveFilterValue('fullOrEmpty', 'F');
        } else if (this.shipmentType === 'NONCNTC' && this.additionalServices !== null) {
            if (this.additionalServices.some((x) => x.additionalServiceId === 'CONTE')) {
                this.setInactiveFilterValue('fullOrEmpty', 'E');
            } else if (this.additionalServices.some((x) => x.additionalServiceId === 'DECON')) {
                this.setInactiveFilterValue('fullOrEmpty', 'F');
            }
        } else if (this.shipmentType === 'EMPTYCNT') {
            this.setInactiveFilterValue('fullOrEmpty', 'E');
        }

        if (this.shippingLineId) {
            this.setReleaseOrderControls();
        }

        if (this.vesselVisitId) {
            const control = this.form.controls['vesselVisitId'];
            control.setValue(this.vesselVisitId);
            if (this.releaseOrderId) control.disable();
        }

        if (this.voyageNumber) {
            const control = this.releaseOrderId
                ? this.form.controls['unloadingVoyageNumber']
                : this.form.controls['loadingVoyageNumber'];
            control.setValue(this.voyageNumber);
            if (this.releaseOrderId) control.disable();
        }
    }

    public selectionCallback(context: RowArgs) {
        return context.dataItem.id;
    }

    save() {
        if (this.disabledSocImportSelection()) {
            return;
        }
        this.activeModal.close(this.fetchSelectedItems());
    }

    fetchSelectedItems(): any {
        const selection = {
            serviceRequestId: this.serviceRequestId,
            containers: this.selected
        };

        if (!this.appliedSocImportSearch) {
            return selection;
        }

        const criteria = this.appliedSearchCriteria;
        return {
            ...selection,
            line: criteria.line?.id ?? criteria.line,
            containerNumbers: criteria.containers,
            billsOfLading: criteria.billsOfLading,
            releaseOrderNumber: criteria.releaseOrderNumber
        };
    }

    disabledSearch() {
        // // VSIN
        // if (this.manipulationId == ManipulationCodes.unloadingVessel) {
        //     return this.form.controls['vesselVisitVoyage'].value == null || this.form.controls['vesselVisitVoyage'].value == "" ||
        //         this.form.controls['billOfLading'].value == null || this.form.controls['billOfLading'].value == "" ||
        //         this.form.controls['line'].value == null || this.form.controls['line'].value == "" ||
        //         this.form.controls['fe'].value == null || this.form.controls['fe'].value == "";
        // }

        // // TRIN/RLIN
        // else if (this.manipulationId == ManipulationCodes.TRIN || this.manipulationId == ManipulationCodes.RLIN) {
        //     // return this.form.controls['booking'].value == null || this.form.controls['booking'].value == "" ||
        //     return this.form.controls['line'].value == null || this.form.controls['line'].value == "" ||
        //         this.form.controls['fe'].value == null || this.form.controls['fe'].value == "";
        //     // this.form.controls['containers'].value == null || this.form.controls['containers'].value == "";
        // }

        // // VSOUT
        // else if (this.manipulationId == ManipulationCodes.loadingVessel) {
        //     return this.form.controls['vesselVisitVoyage'].value == null || this.form.controls['vesselVisitVoyage'].value == "" ||
        //         // this.form.controls['booking'].value == null || this.form.controls['booking'].value == "" ||
        //         this.form.controls['line'].value == null || this.form.controls['line'].value == "" ||
        //         this.form.controls['fe'].value == null || this.form.controls['fe'].value == "";
        // }

        // // TROUT/RLOUT
        // else if (this.manipulationId == ManipulationCodes.TROUT || this.manipulationId == ManipulationCodes.RLOUT) {
        //     return this.form.controls['vesselVisitVoyage'].value == null || this.form.controls['vesselVisitVoyage'].value == "" ||
        //         this.form.controls['billOfLading'].value == null || this.form.controls['billOfLading'].value == "" ||
        //         this.form.controls['line'].value == null || this.form.controls['line'].value == "" ||
        //         this.form.controls['fe'].value == null || this.form.controls['fe'].value == "";
        //     // this.form.controls['containers'].value == null || this.form.controls['containers'].value == "";
        // }

        return !this.form.valid;
    }

    isRowSelected = (e: RowArgs) => this.selected.indexOf(e.dataItem.id) >= 0;

    clearSelection() {
        this.selected = [];
    }

    rowCallback = (context: RowClassArgs) => {
        const isSelected = this.selected.indexOf(context.dataItem.id) >= 0;
        return {
            blueColoredRow: isSelected,
            uncolored: !isSelected
        };
    };

    // angular upgrade fix
    keyChange(e: any) {
        this.selected = e;
    }

    private includeReleaseOrderCheck() {
        return (
            ((this.shipmentType == 'FULLCNT' || this.shipmentType == 'EMPTYCNT') &&
                ['ADDS', 'FCFS', 'RLOUT', 'TCFS', 'TROUT'].includes(this.manipulationId)) ||
            this.additionalServices?.some((x) => ['DECON', 'CONTE'].includes(x.additionalServiceId))
        );
    }

    private setReleaseOrderControls() {
        // Ensure form is initialized before proceeding
        if (!this.form?.controls) {
            return;
        }

        this.form.controls['line'].setValue(this.shippingLineId);
        this.form.controls['line'].disable();
        this.form.controls['line'].updateValueAndValidity();
        this.form.controls['booking'].disable();
        this.bookingVisible = false;

        const editableROTypes = ['STUF', 'CUSI'];

        if (!this.releaseOrderTypeId) {
            return;
        }

        if (this.releaseOrderTypeId === ReleaseOrderTypeCodes.Soc) {
            this.form.controls['fullOrEmpty'].setValue(null);
            this.setInactiveFilterValue('fullOrEmpty', null);
            this.form.controls['fullOrEmpty'].enable();
            return;
        }

        if (editableROTypes.includes(this.releaseOrderTypeId)) {
            // Editable: STUF (Conte) -> E, CUSI (Customs Insp.) -> F
            const defaultFE = this.releaseOrderTypeId === 'STUF' ? 'E' : 'F';
            this.form.controls['fullOrEmpty'].setValue(defaultFE);
            this.setInactiveFilterValue('fullOrEmpty', defaultFE);
            this.form.controls['fullOrEmpty'].enable();
            return;
        }

        // Non-editable: FUL (Full) & STR (Decont) -> F, EMP (Empty) → E
        const defaultFE = this.releaseOrderTypeId === 'FUL' || this.releaseOrderTypeId === 'STR' ? 'F' : 'E';
        this.form.controls['fullOrEmpty'].setValue(defaultFE);
        this.setInactiveFilterValue('fullOrEmpty', defaultFE);
        this.form.controls['fullOrEmpty'].disable();
    }

    selectAllContainerVisits() {
        if (
            this.isBusy ||
            (!this.appliedSearchCriteria && this.disabledSearch()) ||
            this.disabledSocImportSelection()
        ) {
            return;
        }
        if (!this.selectAll) {
            const criteriaVersion = this.socImportCriteriaVersion;
            this.isBusy = true;
            this.serviceRequestService
                .getContainerVisitIds(this.filter)
                .pipe(finalize(() => (this.isBusy = false)))
                .subscribe((data) => {
                    if (criteriaVersion === this.socImportCriteriaVersion) {
                        this.selectAll = true;
                        this.selected = data;
                    }
                });
        } else {
            this.selected = [];
            this.selectAll = false;
        }
    }

    getClass(status: string): string {
        return this.resolveVisitStatusPillClass(status);
    }

    private updateVoyageControl(control: AbstractControl<string | null>, voyageNumber: string | null): void {
        if (voyageNumber) {
            control.setValue(voyageNumber);
            control.disable();
        } else {
            control.setValue(null);
            control.enable();
        }
    }

    private populateVoyageNumber(vesselVisitId: number): void {
        let voyageControl: AbstractControl<any, any>;

        if (this.isVSOUTServiceRequest) {
            voyageControl = this.form.controls['loadingVoyageNumber'];
        } else if (this.showUnloadingVoyageNumber) {
            voyageControl = this.form.controls['unloadingVoyageNumber'];
        }

        if (!voyageControl) {
            return;
        }

        if (!vesselVisitId) {
            this.updateVoyageControl(voyageControl, null);
            return;
        }

        this.serviceRequestService
            .getVoyageNumberFromVesselVisit({ vesselVisitId: vesselVisitId, manipulationId: this.manipulationId })
            .subscribe((response) => {
                this.updateVoyageControl(voyageControl, response?.voyageNumber);
            });
    }

    private populateReleaseOrderVoyageNumber(vesselVisitId: number): void {
        const voyageControl = this.form.controls['unloadingVoyageNumber'];

        if (!vesselVisitId) {
            this.updateVoyageControl(voyageControl, null);
            return;
        }

        this.serviceRequestService
            .getVoyageNumberFromVesselVisit({ vesselVisitId: vesselVisitId })
            .subscribe((response) => {
                this.updateVoyageControl(voyageControl, response?.voyageNumber);
            });
    }
}
